<script setup lang="ts">
  import { InboundEmailsService, type SuperUserInboundEmail } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'

  const fetchInboundEmails = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<SuperUserInboundEmail>> => {
    return InboundEmailsService.inboundEmailsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    }) as unknown as PaginatedResponse<unknown>
  }
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Emails', to: ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_LIST }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  :fetchData="fetchInboundEmails"
                  searchableFields="From, To, Content"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Failed</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: emails }: { items: SuperUserInboundEmail[] }">
                    <TableRow
                      v-for="email in emails"
                      :key="email.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_DETAIL.replace(':uuid', email.uuid)"
                        >
                          {{ email.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        {{ email.created_at ? formatDateTimeGerman(email.created_at) : '-' }}
                      </TableCell>
                      <TableCell>
                        {{ email.from_email.email }}
                      </TableCell>
                      <TableCell>
                        <span
                          v-for="to in email.to"
                          :key="to.email"
                        >
                          {{ to.email }}
                        </span>
                      </TableCell>
                      <TableCell>
                        {{ email.is_failed ? 'Yes' : 'No' }}
                      </TableCell>
                      <TableCell>
                        <router-link
                          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                            text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50
                            dark:text-blue-500 dark:hover:text-blue-400"
                          :to="ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_DETAIL.replace(':uuid', email.uuid)"
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
